import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

export const NotificationsBell = () => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  const load = () => api.get("/notifications")
    .then((r) => { setItems(r.data.notifications || []); setUnread(r.data.unread || 0); })
    .catch(() => {});
  useEffect(() => { load(); }, []);

  const [open, setOpen] = useState(false);
  const onOpenChange = (o) => {
    setOpen(o);
    if (o && unread > 0) {
      api.post("/notifications/mark-read").then(() => setUnread(0)).catch(() => {});
    }
  };

  const go = (link) => {
    if (!link) return;
    setOpen(false);
    navigate(link);
  };

  const sayThanks = (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, thanked: true } : n)));
    api.post(`/notifications/${id}/kudos-thanks`).catch(() => {});
  };

  const clearOne = (id) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
    api.delete(`/notifications/${id}`).catch(() => {});
  };
  const clearAll = () => {
    setItems([]);
    setUnread(0);
    api.delete("/notifications/clear-all").catch(() => {});
  };

  if (items.length === 0) return null;

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notifications-bell"
          className="relative flex items-center px-2.5 py-1.5 rounded-full bg-white border border-[#C4C8CE] hover:bg-[#F4F4F5] transition-colors cursor-pointer"
          title="Notifications">
          <Bell size={18} weight="duotone" color="#0A0D14" />
          {unread > 0 && (
            <span data-testid="notifications-unread" className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#FF5100] text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto" data-testid="notifications-list">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          <span role="button" tabIndex={0} data-testid="notifications-clear-all"
            className="text-[11px] font-semibold text-[#00768a] cursor-pointer hover:underline normal-case"
            onClick={(e) => { e.stopPropagation(); clearAll(); }}>
            Clear all
          </span>
        </DropdownMenuLabel>
        {items.map((n) => (
          <button key={n.id} type="button" data-testid={`notification-${n.id}`}
            className="w-full text-left px-2 py-2 rounded-md hover:bg-[#F4F4F5] cursor-pointer border-0 bg-transparent"
            onClick={() => go(n.link)}>
            <div className="flex items-start gap-2">
              {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#FF5100] shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-black truncate">{n.title}</div>
                <div className="text-xs text-black">{n.body}</div>
                <div className="text-[10px] text-black">{new Date(n.created_at).toLocaleString()}</div>
                {n.link && (
                  <span role="button" tabIndex={0} data-testid={`notification-go-${n.id}`}
                    className="inline-block mt-1 text-[12px] font-semibold text-blue-600 underline cursor-pointer hover:text-blue-800"
                    onClick={(e) => { e.stopPropagation(); go(n.link); }}>
                    Go There Now
                  </span>
                )}
                {n.type === "kudos" && n.kudos_id && (
                  n.thanked ? (
                    <span className="inline-block mt-1 text-[11px] font-semibold text-[#3F6212]" data-testid={`kudos-thanked-${n.id}`}>🙏 Thanks sent ✓</span>
                  ) : (
                    <span role="button" tabIndex={0} data-testid={`kudos-thanks-${n.id}`}
                      className="inline-block mt-1 px-2 py-1 rounded-full bg-[#CCFF00] text-[#000000] text-[11px] font-bold cursor-pointer hover:brightness-95"
                      onClick={(e) => { e.stopPropagation(); sayThanks(n.id); }}>
                      🙏 Say thanks
                    </span>
                  )
                )}
              </div>
              <span role="button" tabIndex={0} data-testid={`notification-clear-${n.id}`} title="Clear this notification"
                className="shrink-0 p-1 rounded-full text-[#000000] hover:text-[#000000] hover:bg-[#ECEDEF] cursor-pointer"
                onClick={(e) => { e.stopPropagation(); clearOne(n.id); }}>
                <X size={13} weight="bold" />
              </span>
            </div>
          </button>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
